import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Col, Row} from "reactstrap";
import Button from "../../../../../components/base/button/button";
import {Add, OpenInNew} from "@mui/icons-material";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import PartnerDataGrid from "../../../../../containers/data-grid";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import useSearch from "../../../../../hooks/use-search";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import UpsertNCRDialog from "../../../../../components/dialogs/upsert-ncr";
import QueryBuilder, {QueryBuilderUtils} from "../../../../../../packages/query-builder";
import {createUUId, numComparator} from "../../../../../../core/services/utils/utils";
import useDynamicColumns, {DynamicColumnApplications} from "../../../../../hooks/use-dynamic-columns";
import useReportingSearchUtils from "../../../../../hooks/use-reporting-search-utils";
import IconButton from "../../../../../components/base/button/icon_button";
import {Link, useHistory} from "react-router-dom";
import LoadingLottieAnimation from "../../../../../components/app-specific/lottie-animations/loading";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import Api from "../../../../../../core/services/api/api";
import ToastService from "../../../../../../core/services/toast";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import Grid from "@mui/material/Unstable_Grid2";
import {Chip, MenuItem, Stack, TextField} from "@mui/material";
import {NCRSettingsTabs} from "../settings";


const queryBuilderSaveName = 'NCRListViewQueryBuilder';
const dynamicColumnsSectionName = 'NcrReport';
const dataGridSaveConfig = {
    name: "NCRListViewDataGrid",
    version: 2,
}

const staticColumnsNames = {
    navigate: 'navigate',
}

const initAddNCRDialogState = {
    open: false,
};

const NCRListView = () => {
    const history = useHistory();
    const isMounted = useIsMounted();
    const [data, setData] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [addNCRDialogState, setAddNCRDialogState] = useState(initAddNCRDialogState);
    const {configureNotebookTags} = useConfigureNotebook('ncr-list');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canExportNcrList = operatorAccessLevels.packages.ncr.exportNcrList;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.ncrList);
        configureBehavioralTool((tags) => tags.ncrList);
    }, [configureBehavioralTool, configureNotebookTags]);

    const dataGridId = useMemo(() => createUUId(true), []);
    const staticDataGridColumns = useMemo(() => [
        ...(operatorAccessLevels.packages.ncr.viewDetails ? [
            {
                name: staticColumnsNames.navigate,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                width: 0,
            }
        ] : []),
    ], [operatorAccessLevels.packages.ncr.viewDetails]);

    const dataGridColumnsConfig = useMemo(() => ({}), [])

    const [dynamicColumnsLoading, _queryBuilderColumns, _dataGridColumns] = useDynamicColumns({
        applicationName: DynamicColumnApplications.BizNcr,
        sectionName: dynamicColumnsSectionName,
        staticDataGridColumns: staticDataGridColumns,
        dataGridColumnsConfig: dataGridColumnsConfig,
    });

    const dataGridColumns = useMemo(() =>
        _dataGridColumns
            .filter(e => !['statusId', 'createdByOperatorLastName', "reportedByOperatorLastName"].includes(e.name))
            .map((e => ({
                ...e,
                title: e.name === "createdByOperatorFirstName"
                    ? "Creator"
                    : e.name === "reportedByOperatorFirstName"
                        ? "Reporter"
                        : e.title,
                sortable: e.name === 'status' ? false : e.sortable,
            }))), [_dataGridColumns]);
    const queryBuilderColumns = useMemo(() => _queryBuilderColumns.filter(e => e.field !== 'statusId' && e.field !== 'status'), [_queryBuilderColumns]);

    const [savedDataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: dataGridColumns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])


    /**
     * Fetches the entries from the server based on the user's filters.
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const siftedFilters = QueryBuilderUtils.getApiQueryFromStateQuery(args.filters, queryBuilderColumns);
        const forApi = {
            reportingBaseModel: {
                keyword: siftedFilters.keyword,
                filters: siftedFilters.filters ?? [],
            },
            statusIds: args.filters?.content?.statuses?.map(e => e.id) ?? [],
            pagination: args.paginationInfo,
            sorts: args.orderBy ? [{isDesc: args.orderBy.descending, property: args.orderBy.field,}] : [],
            // sorts: [],
        }
        const response = await Api.listNcrReports(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data?.items ?? []);
            args.setPaginationInfo((prevState) => ({
                ...prevState,
                length: response.data?.totalCount ?? 0,
            }));
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, queryBuilderColumns])

    /**
     * Searches among the entries based on the filters from the user.
     */
    const search = useCallback(async (args) => {
        await fetchEntries(args, false);
    }, [fetchEntries]);

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {},
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)
    const {setPaginationInfo} = searchData;

    /**
     * Formats the exported cell based on the column type.
     */
    const getDataGridCell = useCallback((row, column, continueMarker) => {
        switch (column.name) {
            case staticColumnsNames.navigate:
                return (
                    <IconButton
                        color={'secondary'}
                        component={Link}
                        to={routeFunctions.ncr.single(row.id)}
                    >
                        <OpenInNew className='icon normal'/>
                    </IconButton>
                )
            case 'code':
                return ` ${row.code} ` ?? continueMarker;
            case "createdByOperatorFirstName":
                return [row.createdByOperatorFirstName, row.createdByOperatorLastName].filter(Boolean).join(' ');
            case "reportedByOperatorFirstName":
                return [row.reportedByOperatorFirstName, row.reportedByOperatorLastName].filter(Boolean).join(' ');
            default:
                return continueMarker
        }
    }, [])


    const {
        getExportInfo,
        dataGridRows,
    } = useReportingSearchUtils({
        savedDataGridColumns,
        fetchEntries,
        searchData,
        getDataGridCell,
        data,
        exportingTitle: 'NCR List'
    });

    /**
     * adds the newly created report to the table data
     * @param newReport {any} newly created report
     */
    const onAddSuccess = useCallback((newReport) => {
        if (!!newReport?.id) {
            // navigate to the new report
            history.push(routeFunctions.ncr.single(newReport?.id));
        } else {
            setData(prevState => {
                if (prevState.find((e) => e.id === newReport.id)) {
                    return prevState.map((e) => (e.id === newReport.id ? newReport : e));
                }
                setPaginationInfo(prevState => ({...prevState, length: prevState.length + 1}));
                return [newReport, ...prevState];
            })
        }
    }, [history, setPaginationInfo]);

    /**
     * Opens the dialog to add a new report
     * @type {(function(): Promise<undefined|void>)|*}
     */
    const openDialogToAddReport = useCallback(async () => {
        const response = await Api.getNcrInitialStatus(false)
        if (!isMounted())
            return
        if (!response?.data) {
            ToastService.warning({title: "Status Needed", message: "Please create one status prior to create a report"});
            return history.push(routeFunctions.ncr.settings(NCRSettingsTabs.statuses.value));
        }
        setAddNCRDialogState({open: true})
    }, [history, isMounted])

    /**
     * Fetches the ncr statuses of the system.
     * @type {(function(): Promise<void>)}
     */
    const getStatuses = useCallback(async () => {
        const response = await Api.listNcrStatuses();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const res = [...response.data];
            res.sort((a, b) => numComparator(a.orderIndex, b.orderIndex));
            setStatuses(res);
        }
    }, [isMounted])

    /**
     * As soon as the dialog is opened:
     * - Get the statuses
     */
    useEffect(() => {
        void getStatuses();
    }, [getStatuses]);

    return (
        <>
            <Row className="no-gutters w-100 justify-content-between align-items-center">
                <Col xs={6}>
                    <h1 className={'text-xl font-weight-semi-bold '}>
                        NCR Reports
                    </h1>
                </Col>
                {
                    operatorAccessLevels.packages.ncr.manageReport &&
                    <Col xs={6} className="row no-gutters w-100 justify-content-end align-items-center">
                        <Button
                            className={"mr-2 d-flex align-items-center justify-content-center"}
                            variant={"outlined"}
                            color={"primary"}
                            onClick={openDialogToAddReport}
                        >
                            <Add className={`icon small`} style={{margin: "2px 0"}}/>
                            <p className="ml-2">Add new</p>
                        </Button>
                    </Col>
                }
            </Row>
            {
                dynamicColumnsLoading &&
                <>
                    <LoadingLottieAnimation
                        width={'80%'}
                        className={'my-5 py-5'}
                    />
                </>
            }
            {
                !dynamicColumnsLoading &&
                <>
                    <QueryBuilder
                        columns={queryBuilderColumns}
                        name={queryBuilderSaveName}
                        search={(filters) => searchData.setFilters(prevState => ({...prevState, ...filters}))}
                        query={searchData.filters}
                        loading={searchData.loading}
                        dataGridId={dataGridId}
                    >
                        {({setContent, content}) => (
                            <>
                                <Grid xs={12}>
                                    <TextField
                                        select
                                        label={'Statuses'}
                                        variant={'outlined'}
                                        size={'small'}
                                        margin={'dense'}
                                        fullWidth
                                        value={statuses.filter(e => content.statuses?.find(s => e.id === s.id))}
                                        onChange={(e) => setContent((prevState) => ({...prevState, statuses: e.target.value ?? []}))}
                                        SelectProps={{
                                            multiple: true,
                                            renderValue: (statuses) =>
                                                <Stack flexDirection={'row'} flexWrap={'wrap'} gap={0.5}>
                                                    {
                                                        statuses
                                                            .slice(0, 2)
                                                            .map((e) => e.title).map(e => (
                                                            <Chip
                                                                key={e}
                                                                label={e}
                                                                size={'small'}
                                                            />
                                                        ))
                                                    }
                                                    {
                                                        statuses.length > 2 &&
                                                        <Chip
                                                            label={`+${statuses.length - 2}`}
                                                            size={'small'}
                                                            color={'primary'}
                                                        />
                                                    }
                                                </Stack>
                                        }}
                                    >
                                        {
                                            statuses.map(e => (
                                                <MenuItem key={e.id} value={e}>
                                                    {e.title}
                                                </MenuItem>
                                            ))
                                        }
                                    </TextField>
                                </Grid>

                            </>
                        )}
                    </QueryBuilder>
                    <div className='w-100 my-2'/>
                    <PartnerDataGrid
                        id={dataGridId}
                        {...dataGridSaveConfig}
                        initialLayoutState={{columns: dataGridColumns}}
                        columns={savedDataGridColumns}
                        rows={dataGridRows}
                        hideFooterActions
                        state={{
                            sortBy: searchData.orderBy,
                            pagination: searchData.paginationInfo,
                            loading: {state: searchData.loading},
                            density: dataGridDensity,
                        }}
                        getExportInfo={canExportNcrList ? getExportInfo : undefined}
                        onSortByChanged={(_, current) =>
                            searchData.setOrderBy?.(current)}
                        onCurrentPageChanged={(_, current) =>
                            searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                        onPageSizeChanged={(_, current) =>
                            searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    />
                    <Col className={'mt-4'}/>
                </>
            }
            {
                operatorAccessLevels.packages.ncr.manageReport &&
                <UpsertNCRDialog
                    open={addNCRDialogState.open}
                    setOpen={() => setAddNCRDialogState(initAddNCRDialogState)}
                    onSuccess={onAddSuccess}
                />
            }
        </>
    );
};

export default NCRListView;
